// Warehouse Equipment Integration Service
// This service handles the integration between warehouse inventory and equipment management

import axios from 'axios';
import { getCachedSetting } from '../utils/categoryEquipmentCache';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

/**
 * Fetches warehouse items that should be displayed in equipment management
 * Only returns items from categories with show_in_equipment = true
 */
export const fetchWarehouseItemsForEquipment = async () => {
  try {
    const token = localStorage.getItem('access_token');
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
    };

    // First, fetch all categories
    const categoriesResponse = await axios.get(`${API_BASE_URL}/api/warehouse/categories/`, config);
    const allCategories = Array.isArray(categoriesResponse.data) ? categoriesResponse.data :
                         categoriesResponse.data.results ? categoriesResponse.data.results : [];

    // Filter categories that have show_in_equipment = true (using cache since backend doesn't support it)
    const equipmentCategories = allCategories.filter(cat => {
      // Check cached value since backend doesn't have show_in_equipment field yet
      const showInEquipment = cat.show_in_equipment || getCachedSetting(cat.id);
      console.log(`Category ${cat.name} (${cat.id}): show_in_equipment = ${showInEquipment}`);
      return showInEquipment === true;
    });

    if (equipmentCategories.length === 0) {
      return [];
    }

    // Fetch items from these categories
    const categoryIds = equipmentCategories.map(cat => cat.id);
    const itemsPromises = categoryIds.map(categoryId =>
      axios.get(`${API_BASE_URL}/api/warehouse/items/?category=${categoryId}`, config)
    );

    const itemsResponses = await Promise.all(itemsPromises);
    const allItems = [];

    itemsResponses.forEach(response => {
      const items = Array.isArray(response.data) ? response.data :
                   response.data.results ? response.data.results : [];
      allItems.push(...items);
    });

    return allItems;
  } catch (error) {
    console.error('Error fetching warehouse items for equipment:', error);
    return [];
  }
};

/**
 * Updates the show_in_equipment flag for a category
 */
export const updateCategoryEquipmentVisibility = async (categoryId, showInEquipment) => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await axios.patch(
      `${API_BASE_URL}/api/warehouse/categories/${categoryId}/`,
      { show_in_equipment: showInEquipment },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating category equipment visibility:', error);
    throw error;
  }
};

/**
 * Converts warehouse item to equipment format
 */
export const convertWarehouseItemToEquipment = (item, userCompany) => {
  return {
    id: `warehouse-${item.id}`,
    safety_equipment_serial_number: item.sku || `WH-${item.id}`,
    safety_equipment_type_name: item.category_name || 'Warehouse Item',
    safety_equipment_description: item.name,
    safety_equipment_model_name: item.unit || 'Standard',
    safety_equipment_purchase_date: item.created_at || new Date().toISOString(),
    safety_equipment_inspection_validity_months: 12, // Default inspection period
    safety_equipment_manufacturer_validity_months: 24, // Default manufacturer validity
    safety_equipment_status: item.quantity_in_stock > 0 ? 'available' : 'unavailable',
    safety_equipment_location: item.location || 'Warehouse',
    safety_equipment_company_name: item.owner_company_name || userCompany,
    safety_equipment_notes: `Warehouse item - Stock: ${item.quantity_in_stock}`,
    is_from_warehouse: true,
    warehouse_item_id: item.id,
    quantity_in_stock: item.quantity_in_stock,
    unit_price: item.unit_price
  };
};

export default {
  fetchWarehouseItemsForEquipment,
  updateCategoryEquipmentVisibility,
  convertWarehouseItemToEquipment
};